import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const registrationPasswordRequirementDefs = [
  {
    id: 'length',
    label: 'At least 8 characters',
    meets: (value: string) => value.length >= 8,
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    meets: (value: string) => /[a-z]/.test(value),
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    meets: (value: string) => /[A-Z]/.test(value),
  },
  {
    id: 'number',
    label: 'One number',
    meets: (value: string) => /[0-9]/.test(value),
  },
  {
    id: 'special',
    label: 'One special character (e.g. !@#$%)',
    meets: (value: string) => /[^A-Za-z0-9]/.test(value),
  },
] as const;

export type RegistrationPasswordRequirementId =
  (typeof registrationPasswordRequirementDefs)[number]['id'];

export function getRegistrationPasswordRequirementStates(
  password: string,
): ReadonlyArray<{
  id: RegistrationPasswordRequirementId;
  label: string;
  met: boolean;
}> {
  return registrationPasswordRequirementDefs.map(({ id, label, meets }) => ({
    id,
    label,
    met: meets(password),
  }));
}

export const registrationPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .regex(/[a-z]/, 'Password must include at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must include at least one uppercase letter')
  .regex(/[0-9]/, 'Password must include at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must include at least one special character');

export const registerSchema = z
  .object({
    displayName: z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .max(80, 'Name is too long'),
    email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
    password: registrationPasswordSchema,
    confirmPassword: z.string().min(1, 'Confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type LoginFormValues = z.infer<typeof loginSchema>;
export type RegisterFormValues = z.infer<typeof registerSchema>;
